import PmParcel from 'interfaces/PmParcel';
import { FunctionComponent } from 'preact';
import { TrackingTimeline } from 'components/MerchantTrackingWidget/components/TrackingTimeline';
import { useFeatureToggleContext } from 'FeatureConfig.context';
import { usePTPConfigContext } from 'GlobalConfig.context';
import { StepPendingApproval } from '../StepPendingApproval';
import { StepReturnCancelled } from '../StepReturnCancelled';
import { StepReturnRejected } from '../StepReturnRejected';
import { StepDownloadInstructions } from '../StepDownloadInstructions';
import { StepReturnApproved } from '../StepReturnApproved';
import { ReturnShipment } from '../../../../models/ReturnShipment.model';

import {
  StepContainerFullHeight,
  TrackingTimelineContainer,
} from './MainInfo.styles';

interface MainInfoProps {
  shipment: PmParcel;
}

const MainInfo: FunctionComponent<MainInfoProps> = ({
  shipment: baseShipment,
}) => {
  const shipment = ReturnShipment.create(baseShipment, { partial: false });
  const { returnTicket } = shipment;
  const {
    returnManagement: { enablePreprintedReturnLabel },
  } = useFeatureToggleContext();
  const {
    return: { returnFlow },
  } = usePTPConfigContext();
  const isDropOffOptional = returnFlow?.dropOffSelection?.isOptional ?? false;
  const isAutoBookingPending =
    returnTicket.isBookingPending() && isDropOffOptional;

  const shouldShowTrackingTimeline = enablePreprintedReturnLabel
    ? shipment.hasPhaseDOrLater()
    : shipment.hasPickupEvents();
  const events = enablePreprintedReturnLabel
    ? shipment.parcel.events
    : shipment.mixedEvents;

  // step Cancel -> use click Cancel return request
  if (returnTicket.isCancelled()) {
    return (
      <StepContainerFullHeight>
        <StepReturnCancelled />
      </StepContainerFullHeight>
    );
  }

  // step Pending Approval
  if (returnTicket.isPending()) {
    return (
      <StepContainerFullHeight>
        <StepPendingApproval />
      </StepContainerFullHeight>
    );
  }

  // Failed booking (manual/optional flow) or auto-booking pending/in-flight → show instructions.
  // NOTE: isFailedBooking() and isBookingPending() are only possible when the ticket is already Approved
  if (returnTicket.isFailedBooking() || isAutoBookingPending) {
    return <StepDownloadInstructions shipment={shipment} />;
  }

  if (returnTicket.isApproved()) {
    return (
      <StepContainerFullHeight>
        <StepReturnApproved />
      </StepContainerFullHeight>
    );
  }

  // step Rejected
  if (returnTicket.isRejected()) {
    return (
      <StepContainerFullHeight>
        <StepReturnRejected returnTicket={shipment.returnTicket} />
      </StepContainerFullHeight>
    );
  }

  // step Shipping -> after user confirm drop off location -> success
  if (returnTicket.isShipping() && !shipment.hasPickupEvents()) {
    return <StepDownloadInstructions shipment={shipment} />;
  }

  // show tracking time line when having pickup event.
  // https://www.pivotaltracker.com/story/show/187945742
  if (shouldShowTrackingTimeline) {
    return (
      <TrackingTimelineContainer>
        <TrackingTimeline events={events} />
      </TrackingTimelineContainer>
    );
  }

  return null;
};

export { MainInfo };
